import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import AddressFormatter from '@shopify/address';
import moment from 'moment/moment';
import {
  chunk,
  extend,
  find,
  get,
  groupBy,
  isEmpty,
  join,
  map,
  reduce,
  reverse,
  sortBy,
  split,
  uniqBy
} from 'lodash';

import SelectField from '../../../shared-components/reduxForms/fields/SelectField/SelectField';
import TaxInvoice from '../../../shared-components/invoice/TaxInvoice';
import { PageSpinner } from '../../../shared-components/decorators/spinner';

import Contracts from '../../../../../api/api-contracts';
import { Fees } from '../../../../../api/api-fees';
import { Invoices } from '../../../../../api/api-invoices';

import { InvoiceCard } from './InvoiceCard';
import { PaginationBtn } from '../../../elements-buttons/pagination-button';
import { fetchAddressById } from '../../../app-main/actions/fee-profile';
import { fetchInvoicesByYear } from '../../../app-main/actions/invoices';
import { SalesTaxInvoice } from './SalesTaxInvoice/SalesTaxInvoice';
import { downloadPdf, getTaxDetails } from './invoice.utils';

const usePagination = (total = 0, countPerPage = 1) => {
  const [page, setPage] = useState(1);

  const skip = (page - 1) * countPerPage;
  const pageCount = Math.ceil(total / countPerPage || 1);
  const totalCount = total || 1;
  const limit = countPerPage;
  return { page, setPage, pageCount, totalCount, limit, skip };
};

const TaxInvoicesListComponent = ({
  token,
  locale,
  invoices,
  env,
  profile,
  fees,
  feeProfiles,
  latestYear,
  invoiceYears,
  areInvoicesFetching,
  invoiceCount,
  dispatch,
  intl
}) => {
  const itemsPerPage = 3;
  const [selectedYear, setSelectedYear] = useState('');
  const [allCountries, setAllCountries] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const countries = await new AddressFormatter(locale).getCountries();
        const allCountriesMap = reduce(countries, (countriesObj, country) => {
          countriesObj[country.code] = country;
          return countriesObj;
        }, {});
        setAllCountries(allCountriesMap);
      } catch (e) {
        // Error when getting countries
      }
    })();
  }, []);

  const yearsOptions = map(invoiceYears, (year) => {
    return {
      label: year,
      selectedLabel: (<div><FormattedMessage
        id="hosting.tax_details_page_.year"
        defaultMessage="Year" />: <b>{year}</b></div>),
      value: year
    };
  });

  const formatDate = useCallback((date) => {
    const momentDate = moment(date);
    return {
      month: momentDate.locale(locale).format('MMMM'),
      year: momentDate.locale(locale).format('YYYY'),
      day: momentDate.locale(locale).format('DD')
    };
  }, []);

  const {
    page,
    setPage,
    pageCount,
    totalCount,
    limit,
    skip
  } = usePagination(invoiceCount, itemsPerPage);

  useEffect(() => {
    setSelectedYear(latestYear);
  }, []);

  useEffect(() => {
    if (selectedYear) {
      (async () => {
        const ssoId = get(profile, 'SSOid', '');
        dispatch(fetchInvoicesByYear(ssoId, token, selectedYear, limit, skip));
      })();
    }
  }, [selectedYear, page]);

  useEffect(() => {
    setPage(1);
  }, [selectedYear]);

  const generateSalesTaxInvoice = async (invoiceId, countryName, feeName, year, month, day) => {
    const entries = await new Promise((resolve) => {
      Invoices.fetchInvoiceEntries(token, invoiceId, (response) => {
        if (200 === response.statusCode) {
          resolve(response.entries);
        }
      });
    });
    const spaceRentalEntry = get(find(entries, (entry) => 'SPACE_RENTAL_SALES_TAX' === entry.type) || {}, 'metadata', {});
    const addonsEntry = get(find(entries, (entry) => 'ADD_ON_SALES_TAX' === entry.type) || {}, 'metadata', {});

    const displayDate = `${month}${day ? ` ${day}` : ''}, ${year}`;

    const userFeeProfile = get(feeProfiles, 'invoiceFeeProfile.data', {});
    const userVatId = get(userFeeProfile, 'vat_id', null);

    const vatBusinessName = get(feeProfiles, 'vatAddress.data[0].business_name', '');
    const userOrganization = get(profile, 'data.organization', '');

    const controlId = get(entries, '[0].control_id', null);

    const contracts = await new Promise((resolve) => {
      Contracts.readContracts(controlId, token, (response) => {
        resolve(response);
      });
    });

    if (200 !== contracts.statusCode) {
      return;
    }

    const vatProfileId = get(entries, '[0].fee_profile_id', null);
    const billingProfileId = get(entries, '[0].billing_profile_id', null);
    const feeProfileId = vatProfileId || billingProfileId;

    const guestFeeProfile = await dispatch(fetchAddressById(token, feeProfileId)) || {};

    const localeCode = split(locale, '-', 1)[0];
    const addressFormatter = new AddressFormatter(localeCode);
    const guestAddressFormat = await addressFormatter.format({
      address1: guestFeeProfile.street,
      address2: guestFeeProfile.unit,
      province: guestFeeProfile.state,
      city: guestFeeProfile.city,
      zip: guestFeeProfile.zip,
      country: guestFeeProfile.country
    });

    const guestDetails = {
      usersFullName: `${get(profile, 'data.first_name', '')} ${get(profile, 'data.last_name', '')}`,
      userAddressFormatArray: guestAddressFormat,
      userVatId,
      businessName: vatBusinessName,
      userOrganization,
      invoiceFeeName: feeName
    };

    const { taxNumberName, taxNumber, contactPhoneNumber } = getTaxDetails('US', env?.web);

    const document = <SalesTaxInvoice
      invoiceId={invoiceId}
      date={displayDate}
      invoiceCountry={countryName}
      invoiceFeeName={feeName}
      guestDetails={guestDetails}
      spaceRentalEntry={spaceRentalEntry}
      addonsEntry={addonsEntry}
      contracts={contracts.contracts}
      taxNumberName={taxNumberName}
      taxNumber={taxNumber}
      contactPhoneNumber={contactPhoneNumber}
    />;
    const fileName = `PeerspaceSalesTaxInvoice-${invoiceId}.pdf`;
    const onClone = (clonedDoc) => {
      const clonedPdfPage = clonedDoc.getElementById('invoice-pdf-page');
      clonedPdfPage.style.display = 'block';
      const clonedPdfPageLogo = clonedDoc.getElementById('peerspace-logo');
      clonedPdfPageLogo.style.display = 'block';
    };

    await downloadPdf(document, fileName, onClone);
  };

  const generateInvoicePdf = async (invoiceId, countryName, feeName, year, month, day, isTransactional) => {
    const localeCode = split(locale, '-', 1)[0];
    const userFeeProfile = get(feeProfiles, 'invoiceFeeProfile.data', {});
    const vatBusinessName = get(feeProfiles, 'vatAddress.data[0].business_name', '');
    const userOrganization = get(profile, 'data.organization', '');

    const entries = await new Promise((resolve) => {
      Invoices.fetchInvoiceEntries(token, invoiceId, (response) => {
        if (200 === response.statusCode) {
          resolve(response.entries);
        }
      });
    });

    const uniqEntries = uniqBy(entries, 'control_id');
    const chunks = chunk(uniqEntries, 25);

    const spacesRentalsResponses = await Promise.all(chunks.map(chunk => {
      return new Promise(resolve => {
        const query = join(map(chunk, entry => get(entry, 'control_id')), ',');
        Contracts.fetchSpaceRentalsInfo(token, query ? `?control_ids=${query}` : '', (spaceRentals) => {
          resolve(spaceRentals);
        });
      });
    }));
    const spaceRentals = extend.apply({}, spacesRentalsResponses);

    const entriesGroupedByType = groupBy(entries, 'txn_type');
    const invoiceEntriesByType = {};
    if (!isEmpty(entriesGroupedByType.CHARGE)) {
      invoiceEntriesByType.CHARGE = {
        entries: entriesGroupedByType.CHARGE
      };
    }
    if (!isEmpty(entriesGroupedByType.REFUND)) {
      invoiceEntriesByType.REFUND = {
        entries: entriesGroupedByType.REFUND
      };
    }

    const vatProfileId = get(entries, '[0].fee_profile_id', null);
    const billingProfileId = get(entries, '[0].billing_profile_id', null);
    const feeProfileId = vatProfileId || billingProfileId;

    const feeProfile = await dispatch(fetchAddressById(token, feeProfileId));
    const {
      street = '',
      unit = '',
      city = '',
      state = '',
      country = '',
      zip = ''
    } = feeProfile;

    const regionalFees = await new Promise((resolve) => {
      Fees.fetchRegionalFees(token, { country: country }, (regionalFees) => {
        resolve(regionalFees);
      });
    });

    if (200 === regionalFees.statusCode) {
      const region = get(regionalFees, 'regions[0]', {});
      const isEUCountry = 'EU' === get(region, 'code', '');

      const addressFormatter = new AddressFormatter(localeCode);
      const psAddressObj = isEUCountry ?
        get(env, 'web.ps_addresses.NL', '')
        :
        get(env, `web.ps_addresses[${country}]`, '');

      if (!isEmpty(feeProfile)) {
        const addressFormat = await addressFormatter.format({
          address1: street,
          address2: unit,
          province: state,
          city,
          zip,
          country
        });

        const { taxNumberName, taxNumber } = getTaxDetails(country, env?.web);

        const feeTypes = get(fees, 'feeTypes', []);
        const displayDate = isTransactional
          ? `${month}${day ? ` ${day}` : ''}, ${year}`
          : `${month} ${year}`;

        const document = <TaxInvoice
          invoiceId={invoiceId}
          date={displayDate}
          invoiceCountry={countryName}
          invoiceFeeName={feeName}
          intl={intl}
          profile={profile.data}
          userFeeProfile={userFeeProfile}
          userAddressFormatArray={addressFormat}
          psAddressObj={psAddressObj}
          invoiceEntriesByType={invoiceEntriesByType}
          businessName={vatBusinessName}
          locale={locale}
          spaceRentals={spaceRentals}
          feeTypes={feeTypes}
          userOrganization={userOrganization}
          taxNumber={taxNumber}
          taxNumberName={taxNumberName}
        />;

        const fileName = `PeerspaceTaxInvoice-${invoiceId}.pdf`;
        const onClone = (clonedDoc) => {
          const clonedPdfPage = clonedDoc.getElementById('invoice-pdf-page');
          clonedPdfPage.style.display = 'block';
          const clonedPdfPageLogo = clonedDoc.getElementById('peerspace-logo');
          clonedPdfPageLogo.style.display = 'block';
        };

        await downloadPdf(document, fileName, onClone);
      }
    }
  };

  return (
    <>
      <div className="TaxInvoicesList col-xs-12 col-md-8 no-gutter margin-xl-top relative">
        <div className="flex column" style={{ gap: 16 }}>
          <h5 className="flex middle no-bottom-margin">
            <img className="icon margin-md-right" src="https://assets.peerspace.com/signage.svg?v=rb" alt="signage" />
            <FormattedMessage id="hosting.tax_details_page.tax_invoices" defaultMessage="Tax invoices" />
          </h5>
          <hr className="no-margin" />
          <SelectField
            className="invoice-year-select"
            selectClassNames={{
              valueContainer: () => 'select-value-container',
              dropdownIndicator: () => 'select-dropdown-indicator'
            }}
            input={{
              name: 'year-select',
              value: selectedYear,
              onChange: (invoiceYear) => {
                setSelectedYear(invoiceYear);
              }
            }}
            options={yearsOptions}
            defaultValue={{
              label: selectedYear,
              selectedLabel: (<div>
                <FormattedMessage
                  id="hosting.tax_details_page_.year"
                  defaultMessage="Year" />: <b>{selectedYear}</b></div>
              ),
              value: selectedYear
            }}
            formatOptionLabel={(option, { context }) => {
              return 'menu' === context ? option.label : option.selectedLabel;
            }}
          />
          {areInvoicesFetching ? <div className="relative padding-lg-bottom padding-lg-top"><PageSpinner additionalStyle="gray" /></div>
            : <>
              {map(invoices, (invoice) => {
                const { invoice_id, billing_country, fee_name, created, transactional, listing_title } = invoice;
                const isSalesTaxInvoice = 'US_SALES_TAX' === fee_name;
                const countryName = get(allCountries, `[${billing_country}].name`, '');
                const { year, month, day } = formatDate(created);
                const isHost = _.get(profile, 'data.is_host');

                return <InvoiceCard
                  key={invoice_id}
                  country={countryName}
                  invoiceId={invoice_id}
                  feeName={fee_name}
                  year={year}
                  month={month}
                  day={day}
                  isTransactional={transactional}
                  title={listing_title}
                  isHost={isHost}
                  onDownloadClick={async () => {
                    if (isSalesTaxInvoice) {
                      return generateSalesTaxInvoice(invoice_id, countryName, fee_name, year, month, day);
                    }
                    return generateInvoicePdf(invoice_id, countryName, fee_name, year, month, day, transactional);
                  }}
                />;
              })}
              {invoices &&
                <PaginationBtn
                  first
                  last
                  page={page}
                  maxButtons={4}
                  paginate={({ eventKey }) => { setPage(eventKey); }}
                  limit={limit}
                  pageCount={pageCount}
                  totalCount={totalCount}
                />}
            </>
          }
        </div>
      </div>
      <div id="tax-invoice-container">
        {/* PDF file renders in this container */}
      </div>
    </>
  );
};

TaxInvoicesListComponent.propTypes = {
  locale: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  invoices: PropTypes.object,
  env: PropTypes.object,
  latestYear: PropTypes.string,
  invoiceYears: PropTypes.array,
  areInvoicesFetching: PropTypes.bool,
  invoiceCount: PropTypes.number,
  profile: PropTypes.object,
  fees: PropTypes.object,
  feeProfiles: PropTypes.object,
  intl: intlShape
};

const mapStateToProps = (state) => {
  const { invoices, env, profile, fee_profiles, fees } = state;

  const invoiceYears = reverse(sortBy(get(invoices, 'invoiceYears.data.invoice_years', [])));
  const invoiceCount = get(invoices, 'invoicesByYear.data.invoice_count', 0);
  const invoiceByYear = get(invoices, 'invoicesByYear.data.invoices', []);
  const areInvoicesByYearFetching = get(invoices, 'invoicesByYear.isFetching', false);
  const latestYear = invoiceYears[0];

  return {
    invoiceYears,
    latestYear,
    invoiceCount,
    invoices: invoiceByYear,
    areInvoicesFetching: areInvoicesByYearFetching,
    feeProfiles: fee_profiles,
    env,
    profile,
    fees
  };
};

export const TaxInvoicesList = connect(mapStateToProps)(injectIntl(TaxInvoicesListComponent));
